import { API_CONFIG, API_ENDPOINTS, getHeadersWithOrg, apiCall, authFetch } from './client';
import { PLATFORM_ENDPOINTS } from '../../config/config';
import i18n from '../../i18n';
import {
  Site,
  SiteListResponse,
  ErrorResponse,
  DeleteStatusResponse,
  SiteConfigRequest,
  SiteConfigValidationResponse,
  SiteConfigResponse,
  SiteWidgetConfig,
  SitePromptConfig,
  ProvidersMetadataResponse,
  CacheControlsSettings,
  CacheControlsRequest,
} from '../../types';

// =========================================================================
// API Key types
// =========================================================================

export interface CreateApiKeyResponse {
  key_id: string;
  label: string;
  api_key: string;
  key_prefix: string;
  created_at: string;
}

export interface ApiKeyInfo {
  key_id: string;
  label: string;
  key_prefix: string;
  status: string;
  created_at: string;
  created_by: string | null;
  assigned_to?: string | null;
}

export interface ApiKeyListResponse {
  keys: ApiKeyInfo[];
}

/** An orchestrator domain that still references a site pending deletion. */
export interface OrchestratorRef {
  site_id: string;
  site_name: string;
}

/**
 * Thrown by {@link deleteSite} when the domain is still referenced by one or
 * more orchestrator domains and the delete was not forced. Carries the list so
 * the caller can list the dependents and offer a force-retry.
 */
export class SiteDeletionConflictError extends Error {
  orchestrators: OrchestratorRef[];
  constructor(orchestrators: OrchestratorRef[]) {
    super('Domain is referenced by one or more orchestrator domains');
    this.name = 'SiteDeletionConflictError';
    this.orchestrators = orchestrators;
  }
}

/**
 * Thrown by {@link deleteSite} for any failure other than the orchestrator-
 * membership conflict above (e.g. the LRTF site-operation-lock 409, or any
 * other non-2xx response). Carries the HTTP status so callers can show a
 * more specific message for 409s (a job already in progress) vs. anything
 * else.
 */
export class SiteDeleteError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'SiteDeleteError';
    this.status = status;
  }
}

/**
 * Fetch provider metadata (supported providers, models, and config field schemas).
 * This is a public endpoint -- no authentication required.
 */
export async function fetchProviderMetadata(): Promise<ProvidersMetadataResponse> {
  const response = await fetch(
    `${API_CONFIG.PLATFORM_BASE_URL}${PLATFORM_ENDPOINTS.PROVIDERS_METADATA}`,
    { headers: { 'Content-Type': 'application/json' } },
  );
  if (!response.ok) {
    throw new Error(i18n.t('errors.domains.providerMetadataFetchFailed'));
  }
  return response.json();
}

/**
 * Fetch sites for a specific organization
 */
export async function fetchSitesForOrg(orgId: string): Promise<Site[]> {
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${PLATFORM_ENDPOINTS.SITES(orgId)}`;

  try {
    const response = await authFetch(url, {
      method: 'GET',
      headers: getHeadersWithOrg(),
    });

    if (!response.ok) {
      // Handle 404 gracefully - org might not have any sites
      if (response.status === 404) {
        return [];
      }
      const errorData: ErrorResponse = await response.json();
      throw new Error(errorData.error || `Failed to fetch domains: ${response.status}`);
    }

    const data: SiteListResponse = await response.json();
    return data.sites || [];
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(i18n.t('errors.domains.fetchFailed'));
  }
}

/**
 * Fetch all sites across multiple organizations
 * Aggregates sites from all user's orgs
 */
export async function fetchSitesForOrgs(orgIds: string[]): Promise<Site[]> {
  if (orgIds.length === 0) {
    return [];
  }

  // Fetch sites from all orgs in parallel
  const sitePromises = orgIds.map(orgId => fetchSitesForOrg(orgId));
  const sitesArrays = await Promise.all(sitePromises);

  // Flatten and deduplicate sites by site_id
  const siteMap = new Map<string, Site>();
  for (const sites of sitesArrays) {
    for (const site of sites) {
      if (!siteMap.has(site.site_id)) {
        siteMap.set(site.site_id, site);
      }
    }
  }

  return Array.from(siteMap.values());
}

/**
 * Validate site configuration before creation or update.
 * When editing an existing site, pass `siteId` so the backend can merge
 * REDACTED secret placeholders against the stored configuration.
 */
export async function validateSiteConfig(
  orgId: string,
  config: SiteConfigRequest,
  siteId?: string,
): Promise<SiteConfigValidationResponse> {
  let url = `${API_CONFIG.PLATFORM_BASE_URL}${PLATFORM_ENDPOINTS.VALIDATE_SITE(orgId)}`;
  if (siteId) {
    url += `?siteId=${encodeURIComponent(siteId)}`;
  }
  return apiCall<SiteConfigValidationResponse>(url, {
    method: 'POST',
    headers: getHeadersWithOrg(),
    body: JSON.stringify(config),
  });
}

/**
 * Create a new site with the provided configuration
 */
export async function createSite(
  orgId: string,
  config: SiteConfigRequest
): Promise<SiteConfigResponse> {
  return apiCall<SiteConfigResponse>(
    `${API_CONFIG.PLATFORM_BASE_URL}${PLATFORM_ENDPOINTS.CREATE_SITE(orgId)}`,
    {
      method: 'POST',
      headers: getHeadersWithOrg(),
      body: JSON.stringify(config),
    },
  );
}

/**
 * Get a single site's full configuration (including llm_config and embedding_config)
 */
export async function getSite(siteId: string, orgId: string): Promise<SiteConfigResponse> {
  return apiCall<SiteConfigResponse>(
    `${API_CONFIG.PLATFORM_BASE_URL}${PLATFORM_ENDPOINTS.GET_SITE(siteId)}`,
    {
      method: 'GET',
      headers: getHeadersWithOrg(),
    },
  );
}

/**
 * Update an existing site's configuration
 */
export async function updateSite(
  siteId: string,
  orgId: string,
  config: SiteConfigRequest
): Promise<SiteConfigResponse> {
  return apiCall<SiteConfigResponse>(
    `${API_CONFIG.PLATFORM_BASE_URL}${PLATFORM_ENDPOINTS.UPDATE_SITE(siteId)}`,
    {
      method: 'PUT',
      headers: getHeadersWithOrg(),
      body: JSON.stringify(config),
    },
  );
}

/**
 * Initiate async deletion of a site and all its associated indexed data.
 * Returns a DeleteStatusResponse (HTTP 202 Accepted).
 *
 * When the domain is still referenced by an orchestrator, the backend returns
 * 409 unless `force` is set; that case throws {@link SiteDeletionConflictError}.
 */
export async function deleteSite(
  siteId: string,
  orgId: string,
  force = false,
): Promise<DeleteStatusResponse> {
  let url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.DELETE_SITE(siteId)}`;
  if (force) {
    url += '?force=true';
  }

  try {
    const response = await authFetch(url, {
      method: 'DELETE',
      headers: getHeadersWithOrg(),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      const detail = (body as { detail?: unknown } | null)?.detail;

      // The two 409s are distinguished by body shape: the membership conflict
      // carries a `detail` object with an `orchestrators` array, while the
      // pre-existing LRTF-lock conflict (and every other error) carries a string.
      if (
        typeof detail === 'object' &&
        detail !== null &&
        Array.isArray((detail as { orchestrators?: unknown }).orchestrators)
      ) {
        throw new SiteDeletionConflictError(
          (detail as { orchestrators: OrchestratorRef[] }).orchestrators,
        );
      }

      const errorData = body as ErrorResponse | null;
      const message =
        (typeof detail === 'string' ? detail : undefined) ||
        errorData?.error ||
        `Failed to delete domain: ${response.status}`;
      throw new SiteDeleteError(message, response.status);
    }

    const data: DeleteStatusResponse = await response.json();
    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(i18n.t('errors.domains.deleteFailed'));
  }
}

/**
 * Poll the deletion status for a site.
 * Returns the status while deleting; returns null when the site is fully gone (404).
 */
export async function getDeleteStatus(siteId: string, orgId: string): Promise<DeleteStatusResponse | null> {
  const url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.DELETE_STATUS(siteId)}`;

  try {
    const response = await authFetch(url, {
      method: 'GET',
      headers: getHeadersWithOrg(),
    });

    if (response.status === 404) {
      // Site is fully deleted
      return null;
    }

    if (!response.ok) {
      let message = `Failed to fetch delete status: ${response.status}`;
      try {
        const errorData: ErrorResponse = await response.json();
        message = errorData.detail || errorData.error || message;
      } catch {
        // ignore non-JSON body
      }
      throw new Error(message);
    }

    const data: DeleteStatusResponse = await response.json();
    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(i18n.t('errors.domains.deleteStatusCheckFailed'));
  }
}

/**
 * Fetch the widget configuration for a site.
 */
export async function getWidgetConfig(
  siteId: string,
  orgId: string,
): Promise<SiteWidgetConfig> {
  return apiCall<SiteWidgetConfig>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.WIDGET_CONFIG(siteId)}`,
    {
      method: 'GET',
      headers: getHeadersWithOrg(),
    },
  );
}

/**
 * Create or update the widget configuration for a site.
 */
export async function updateWidgetConfig(
  siteId: string,
  orgId: string,
  config: SiteWidgetConfig,
): Promise<SiteWidgetConfig> {
  return apiCall<SiteWidgetConfig>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.WIDGET_CONFIG(siteId)}`,
    {
      method: 'PUT',
      headers: getHeadersWithOrg(),
      body: JSON.stringify(config),
    },
  );
}

// =========================================================================
// Widget Preview
// =========================================================================

export interface WidgetPreviewResponse {
  preview_url: string;
}

/**
 * Create or update the widget preview page.
 * Returns the full URL to the preview chat.html page.
 */
export async function createWidgetPreview(
  siteId: string,
): Promise<WidgetPreviewResponse> {
  return apiCall<WidgetPreviewResponse>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.WIDGET_PREVIEW(siteId)}`,
    {
      method: 'POST',
      headers: getHeadersWithOrg(),
    },
  );
}

// =========================================================================
// Prompt Configuration
// =========================================================================

export async function getPromptConfig(
  siteId: string,
  orgId: string,
  promptType: string,
): Promise<SitePromptConfig | null> {
  const url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.PROMPT_CONFIG(siteId, promptType)}`;

  try {
    const response = await authFetch(url, {
      method: 'GET',
      headers: getHeadersWithOrg(),
    });

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      const errorData: ErrorResponse = await response.json();
      throw new Error(errorData.detail || errorData.error || `Failed to get prompt config: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) throw error;
    throw new Error(i18n.t('errors.domains.promptConfigFetchFailed'));
  }
}

export async function updatePromptConfig(
  siteId: string,
  orgId: string,
  promptType: string,
  content: string,
): Promise<SitePromptConfig> {
  return apiCall<SitePromptConfig>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.PROMPT_CONFIG(siteId, promptType)}`,
    {
      method: 'PUT',
      headers: getHeadersWithOrg(),
      body: JSON.stringify({ content }),
    },
  );
}

export async function deletePromptConfig(
  siteId: string,
  orgId: string,
  promptType: string,
): Promise<void> {
  const url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.PROMPT_CONFIG(siteId, promptType)}`;

  try {
    const response = await authFetch(url, {
      method: 'DELETE',
      headers: getHeadersWithOrg(),
    });

    if (!response.ok && response.status !== 404) {
      const errorData: ErrorResponse = await response.json();
      throw new Error(errorData.detail || errorData.error || `Failed to delete prompt config: ${response.status}`);
    }
  } catch (error) {
    if (error instanceof Error) throw error;
    throw new Error(i18n.t('errors.domains.promptConfigDeleteFailed'));
  }
}

// =========================================================================
// Cache Controls
// =========================================================================

export async function getCacheSettings(
  siteId: string,
  orgId: string,
): Promise<CacheControlsSettings> {
  return apiCall<CacheControlsSettings>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.CACHE_SETTINGS(siteId)}`,
    {
      method: 'GET',
      headers: getHeadersWithOrg(),
    },
  );
}

export async function updateCacheSettings(
  siteId: string,
  orgId: string,
  settings: CacheControlsRequest,
): Promise<CacheControlsSettings> {
  return apiCall<CacheControlsSettings>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.CACHE_SETTINGS(siteId)}`,
    {
      method: 'POST',
      headers: getHeadersWithOrg(),
      body: JSON.stringify(settings),
    },
  );
}

// =========================================================================
// API Key Management
// =========================================================================

export async function createApiKey(
  siteId: string,
  label: string,
): Promise<CreateApiKeyResponse> {
  return apiCall<CreateApiKeyResponse>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.API_KEYS(siteId)}`,
    {
      method: 'POST',
      headers: getHeadersWithOrg(),
      body: JSON.stringify({ label }),
    },
  );
}

export async function listApiKeys(
  siteId: string,
): Promise<ApiKeyListResponse> {
  return apiCall<ApiKeyListResponse>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.API_KEYS(siteId)}`,
    {
      method: 'GET',
      headers: getHeadersWithOrg(),
    },
  );
}

export async function deleteApiKey(
  siteId: string,
  keyId: string,
): Promise<void> {
  const url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.API_KEY(siteId, keyId)}`;
  const response = await authFetch(url, {
    method: 'DELETE',
    headers: getHeadersWithOrg(),
  });
  if (!response.ok && response.status !== 404) {
    const errorData: ErrorResponse = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to delete API key: ${response.status}`);
  }
}

export async function assignApiKey(
  siteId: string,
  keyId: string,
  assignedTo: string | null,
): Promise<ApiKeyInfo> {
  return apiCall<ApiKeyInfo>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.API_KEY_ASSIGN(siteId, keyId)}`,
    {
      method: 'PUT',
      headers: getHeadersWithOrg(),
      body: JSON.stringify({ assigned_to: assignedTo }),
    },
  );
}
